import { collectionRef } from "@/api/collectionRef/collectionOperations";
import { Button, Spinner, Tooltip } from "@radix-ui/themes";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { createToast } from "@/utils/toaster/createToast";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { participateInChallenge } from "../../helperFunctions/participateUserIntoChallenge";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { singleChallengeProps } from "../../utils/singleChallengeProps";
import { Games } from "@/models/database/games/games.model";
import { siteRoutes } from "@/routes/siteRoutesList";
import i18next from "i18next";

export const ParticipateButton: FC<{
  data: singleChallengeProps;
  isPreview?: boolean;
  isText?: boolean;
}> = ({ data, isPreview, isText }): JSX.Element => {
  const navigate = useNavigate();
  const currentUser = useCurrentUserProfile((s) => s.user);
  const [gameData, setGameData] = useState<Games>();

  const [isParticipant, setIsParticipant] = useState<boolean | undefined>(
    data.isCurrUserParticipant
  );

  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);

  const getGameInfo = async () => {
    const games = collectionRef.games;

    const gamesRes = await games
      .where("value", "==", data.challengeData.gameType)
      .limit(1)
      .get();

    if (gamesRes && gamesRes.docs[0] && gamesRes.docs[0].data()) {
      setLoading(false);
      return setGameData(gamesRes.docs[0].data());
    }
    setLoading(false);
    return false;
  };

  useEffect(() => {
    setIsParticipant(data.isCurrUserParticipant);
  }, [data]);

  useEffect(() => {
    getGameInfo();
  }, []);

  const participateCurrentUserIntoThatChallenge = async () => {
    if (!gameData) {
      return createToast({
        type: "error",
        message: "Could not find game info, please try again later",
      });
    }

    setLoading(true);
    if (!currentUser.uid) {
      navigate(siteRoutes["signUp1"]);
      return;
    }

    if (isParticipant) {
      setLoading(false);
      createToast({
        type: "info",
        message: `Already joined \`${data.challengeData.name}\``,
      });
      return;
    }
    if (isPreview) return;

    const res = await participateInChallenge(
      navigate,
      data.challengeData.type,
      gameData,
      currentUser.uid,
      data.challengeData.slug,
      data.challengeData.name
    );
    if (res) {
      setIsParticipant(true);
      // getUserChallData();
      setLoading(false);
    } else {
      setShow(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gameData && !isParticipant && brand.autoJoinChallenge) {
      participateCurrentUserIntoThatChallenge();
    }
  }, [gameData]);

  const userJoined = !gameData?.isManualSubmission && isParticipant;

  if (gameData?.isManualSubmission) {
    return (
      <Tooltip
        content={
          userJoined
            ? "You are currently participating in this challenge."
            : "Press the button to join this challenge."
        }
      >
        <Button
          variant="solid"
          onClick={() =>
            navigate(siteRoutes["submitScoreAndSlug"] + data.challengeData.slug)
          }
          disabled={isPreview || userJoined}
          style={{ width: "100%" }}
        >
          {!loading && (
            <>
              {gameData.isManualSubmission &&
                i18next.t("challenge.buttons.submit")}
            </>
          )}

          {loading && <Spinner />}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      content={
        userJoined
          ? "You are currently participating in this challenge."
          : "Press the button to join this challenge."
      }
    >
      <Button
        variant="solid"
        onClick={participateCurrentUserIntoThatChallenge}
        disabled={isPreview || userJoined}
        style={{ width: "100%" }}
      >
        {!loading && (
          <>
            {!isParticipant && isText && i18next.t("challenge.buttons.join")}
            {!isParticipant &&
              !isText &&
              i18next.t("challenge.buttons.joinThisChallenge")}

            {userJoined && i18next.t("challenge.buttons.joined")}
            {gameData.isManualSubmission &&
              i18next.t("challenge.buttons.submit")}
          </>
        )}

        {loading && <Spinner />}
      </Button>
    </Tooltip>
  );
};
